import { Container, Form, Modal, Text, UseMelioFormResults, useWatch } from '@melio/penny';
import {
  AccountingPlatform,
  AccountingPlatformBillLabel,
  AccountingPlatformBillLineItemLabel,
  AccountingPlatformBillLineItemLabelStatusEnum,
} from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { useBoolean } from '@melio/platform-utils';
import { Dispatch, SetStateAction, useLayoutEffect, useMemo, useState } from 'react';

import {
  AddBillV2FormFrequency,
  AddBillV2FormValues,
  BaseLineItem,
  CategoryBasedBillLineItem,
  ItemBasedBillLineItem,
  XeroSyncedBillLineItem,
} from '../../../types';
import { calculateTotalLineItemsAmount, getTotalLineItemsLength } from '../../../utils';
import { BillDetailsForm } from './components/BillDetailsForm';
import { FormContentDisplay } from './components/FormContentDisplay';
import { LineItemsForm } from './components/ListItemForm';

type Props = {
  hasInvoice: boolean;
  allowRecurring: boolean;
  isMinifiedView: boolean;
  melioFormProps: UseMelioFormResults<AddBillV2FormValues>;
  billLineItemLabels?: AccountingPlatformBillLineItemLabel[];
  billLabel?: AccountingPlatformBillLabel;
  shouldClearVendorIdField?: boolean;
  activeAccountingPlatform?: AccountingPlatform;
  isConnectedToAccountingPlatform: boolean;
  setIsMinifiedView: Dispatch<SetStateAction<boolean>>;
};

export const AddBillFormPanel = ({
  hasInvoice,
  allowRecurring,
  isMinifiedView,
  melioFormProps,
  billLineItemLabels,
  billLabel,
  shouldClearVendorIdField,
  activeAccountingPlatform,
  isConnectedToAccountingPlatform,
  setIsMinifiedView,
}: Props) => {
  const { formatMessage } = useMelioIntl();
  const [amountFieldHelper, setAmountFieldHelper] = useState<string | null>();
  const [shouldShowRecurringLineItemWarningModal, showRecurringLineItemWarningModal] = useBoolean(false);
  const [userAgreedToRecurringPaymentWarningMessage, setUserAgreedToRecurringPaymentWarningMessage] = useState(false);

  const { control, registerField, formProps, setValue, clearErrors, formState } = melioFormProps;
  const [categoryBasedLineItems, itemBasedLineItems, xeroSyncedLineItems, nonSyncedLineItems, amount, frequency] =
    useWatch<AddBillV2FormValues>({
      control,
      name: [
        'categoryBasedLineItems',
        'itemBasedLineItems',
        'xeroSyncedLineItems',
        'nonSyncedLineItems',
        'amount',
        'frequency',
      ],
    });

  const totalAmount = calculateTotalLineItemsAmount({
    categoryBasedLineItems: (categoryBasedLineItems as CategoryBasedBillLineItem[]) ?? [],
    itemBasedLineItems: (itemBasedLineItems as ItemBasedBillLineItem[]) ?? [],
    xeroSyncedLineItems: (xeroSyncedLineItems as XeroSyncedBillLineItem[]) ?? [],
    lineItems: (nonSyncedLineItems as BaseLineItem[]) ?? [],
  });

  const lineItemsLength = getTotalLineItemsLength({
    categoryBasedLineItems: (categoryBasedLineItems as CategoryBasedBillLineItem[]) ?? [],
    itemBasedLineItems: (itemBasedLineItems as ItemBasedBillLineItem[]) ?? [],
    xeroSyncedLineItems: (xeroSyncedLineItems as XeroSyncedBillLineItem[]) ?? [],
    lineItems: (nonSyncedLineItems as BaseLineItem[]) ?? [],
  });

  const isMoreThanOneLineItem = !isMinifiedView && lineItemsLength > 1;

  const isHidden = frequency !== AddBillV2FormFrequency.ONE_TIME;

  const onAmountChange = (amount?: string) => {
    if (amount == null) {
      setValue('amount', '', { shouldDirty: true });
    } else {
      setValue('amount', amount, { shouldDirty: true });
      clearErrors('amount');
    }
    if ((nonSyncedLineItems as BaseLineItem[])?.length == 1) {
      setValue('nonSyncedLineItems.0.amount', amount as string, { shouldDirty: true });
      clearErrors('nonSyncedLineItems.0.amount');
    }

    if (!isMoreThanOneLineItem && !isHidden) {
      if ((itemBasedLineItems as ItemBasedBillLineItem[])?.length == 1) {
        setValue('itemBasedLineItems.0.amount', amount as string, { shouldDirty: true });
      }

      if ((categoryBasedLineItems as CategoryBasedBillLineItem[])?.length == 1) {
        setValue('categoryBasedLineItems.0.amount', amount as string, { shouldDirty: true });
      }

      if ((xeroSyncedLineItems as XeroSyncedBillLineItem[])?.length == 1) {
        setValue('xeroSyncedLineItems.0.amount', amount as string);
      }
    }
  };

  const onCancelFrequencyChange = () => {
    showRecurringLineItemWarningModal.off();
    setValue('frequency', AddBillV2FormFrequency.ONE_TIME, { shouldDirty: true });
  };

  useLayoutEffect(() => {
    if (!totalAmount && !lineItemsLength) {
      setValue('amount', amount as string);
      return;
    }
    if (totalAmount !== parseFloat(amount as string) && (amount !== '' || totalAmount)) {
      setValue('amount', (totalAmount || '') as unknown as string);
      clearErrors('amount');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAmount]);

  useLayoutEffect(() => {
    if (
      isMoreThanOneLineItem &&
      frequency !== AddBillV2FormFrequency.ONE_TIME &&
      !userAgreedToRecurringPaymentWarningMessage
    ) {
      showRecurringLineItemWarningModal.on();
    }

    if (isMoreThanOneLineItem || lineItemsLength) {
      if (amount === totalAmount) {
        setAmountFieldHelper(null);
      } else {
        frequency === AddBillV2FormFrequency.ONE_TIME &&
          setAmountFieldHelper(formatMessage('activities.addBillV2.billForm.billAmount.amountChangedHelperText'));
        if (totalAmount === null) {
          setValue('amount', '0');
        } else {
          setValue('amount', totalAmount as unknown as string);
        }
        clearErrors('amount');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency]);

  const onConfirmFrequencyChangeWarning = () => {
    setUserAgreedToRecurringPaymentWarningMessage(true);
    showRecurringLineItemWarningModal.off();
  };

  const toggleView = () => {
    setIsMinifiedView((lastView) => !lastView);
  };

  const activeBillLabel = useMemo(
    () =>
      billLabel && {
        name: billLabel.name,
        options: billLabel.options.filter(
          (option) =>
            option.status === AccountingPlatformBillLineItemLabelStatusEnum.Active ||
            formState.defaultValues?.externalLabelId === option.id
        ),
      },
    [billLabel, formState.defaultValues]
  );

  return (
    <>
      <Form columns={16} {...formProps} size="small" data-testid="add-bill-v2-form-panel">
        <Form.ContentBox colSpan={16}>
          <Container paddingBottom="s">
            <Text as="h1" textStyle="heading1Semi" aria-live="polite">
              {formatMessage('activities.addBillV2.billForm.title')}
            </Text>
          </Container>
        </Form.ContentBox>
        <FormContentDisplay>
          <BillDetailsForm
            hasInvoice={hasInvoice}
            formControl={control}
            registerField={registerField}
            isMinifiedView={isMinifiedView}
            allowRecurring={allowRecurring}
            shouldClearVendorIdField={shouldClearVendorIdField}
            amountFieldHelper={amountFieldHelper}
            isFormHasLineItems={isMoreThanOneLineItem}
            activeAccountingPlatform={activeAccountingPlatform}
            isConnectedToAccountingPlatform={isConnectedToAccountingPlatform}
            toggleView={toggleView}
            onAmountChange={onAmountChange}
            billLabel={activeBillLabel}
          />
        </FormContentDisplay>
        <FormContentDisplay>
          <LineItemsForm
            setValue={setValue}
            formDefaultValues={formState.defaultValues}
            formControl={control}
            registerField={registerField}
            shouldRender={frequency === AddBillV2FormFrequency.ONE_TIME && !isMinifiedView}
            activeAccountingPlatform={activeAccountingPlatform}
            isConnectedToAccountingPlatform={isConnectedToAccountingPlatform}
            isMinifiedView={isMinifiedView}
            billLineItemLabels={billLineItemLabels}
          />
        </FormContentDisplay>
        <Modal
          shouldHideCloseButton
          isOpen={shouldShowRecurringLineItemWarningModal}
          data-testid="frequency-change-warning-modal"
          header={formatMessage('activities.addBillV2.billForm.frequencyChangeWarningModal.header')}
          description={formatMessage('activities.addBillV2.billForm.frequencyChangeWarningModal.description')}
          primaryButton={{
            variant: 'primary',
            label: formatMessage('activities.addBillV2.billForm.frequencyChangeWarningModal.confirmButton'),
            onClick: onConfirmFrequencyChangeWarning,
          }}
          secondaryButton={{
            variant: 'tertiary',
            label: formatMessage('activities.addBillV2.billForm.frequencyChangeWarningModal.cancelButton'),
            onClick: onCancelFrequencyChange,
          }}
          onClose={showRecurringLineItemWarningModal.off}
        />
      </Form>
    </>
  );
};
